import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpEvent,
  HttpRequest,
  HttpHandler,
  HttpErrorResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';
import { NgZone } from '@angular/core';

@Injectable()
export class TokenInterceptor implements HttpInterceptor {
  private isHandlingAuthFailure = false;

  constructor(
    private afAuth: AngularFireAuth,
    private router: Router,
    private ngZone: NgZone
  ) {}

  private handleInvalidToken(): void {
    if (this.isHandlingAuthFailure) return;
    this.isHandlingAuthFailure = true;

    localStorage.removeItem('user');
    this.afAuth.signOut().catch(() => {});

    this.ngZone.run(() => {
      if (this.router.url !== '/login') {
        this.router.navigate(['login']).finally(() => {
          this.isHandlingAuthFailure = false;
        });
      } else {
        this.isHandlingAuthFailure = false;
      }
    });
  }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const isApi = request.url.includes(environment.api_url);
    if (!isApi) return next.handle(request);

    // 🔒 Only allow through once we have a token
    return this.afAuth.idToken.pipe(
      // Wait until Firebase has actually produced a token
      filter((token): token is string => !!token),
      take(1),
      switchMap((idToken: string) => {
        let headers = request.headers
          .set('X-App-Key', environment.app_token)
          .set('Authorization', `Bearer ${idToken}`);

        if (environment.api_url.includes('ngrok')) {
          headers = headers.set('ngrok-skip-browser-warning', 'true');
        }

        const cloned = request.clone({ headers });
        return next.handle(cloned).pipe(
          catchError((error: HttpErrorResponse) => {
            if (error?.status === 401 || error?.status === 403) {
              this.handleInvalidToken();
            }

            return throwError(() => error);
          })
        );
      })
    );
  }
}
